import { useState } from "react"
import { Building07, ChevronDown } from "@untitledui/icons"
import { motion, AnimatePresence } from "motion/react"
import { useDealershipsForBodyshop } from "@/lib/api/use-teams"
import { useOrganization } from "@/lib/organization/organization-context"
import { TeamMembersTableMini } from "@/components/settings/team/team-members-table-mini"
import { cx } from "@/lib/utils/cx"

// Settings → Dealerships. Visible only when the active team is a body
// shop. Each connected dealership renders as the same expandable card
// the group's org chart uses, so a body-shop user feels like they're
// looking at the same product surface, just from the other direction.
//
// Read-only on the connection itself (the dealership owns connection
// direction), but expanding the card surfaces the dealership's members
// so the body-shop user can see who they're working with.
interface DealershipsSettingsProps {
  bodyshopId?: number | string | null
  bodyshopName?: string
}

export function DealershipsSettings({
  bodyshopId: bodyshopIdProp,
  bodyshopName: bodyshopNameProp,
}: DealershipsSettingsProps = {}) {
  const ctx = useOrganization()
  const bodyshopId = bodyshopIdProp ?? ctx.activeBodyShopId
  const bodyshopName = bodyshopNameProp ?? ctx.activeBodyShop?.name ?? "your body shop"
  const bodyshopIdStr = bodyshopId != null ? String(bodyshopId) : null
  const { data: dealerships, isLoading } = useDealershipsForBodyshop(bodyshopIdStr)
  const items = dealerships ?? []
  void bodyshopName

  return (
    <div>
      <p className="text-[12.5px] text-zinc-500 mb-4">
        {items.length === 0
          ? "Dealerships that send work to you will appear here."
          : `${items.length} ${items.length === 1 ? "dealership sends" : "dealerships send"} work to you.`}
      </p>
      <div>
        {isLoading ? (
          <p className="text-[13px] text-zinc-500 py-6">Loading…</p>
        ) : items.length === 0 ? (
          <EmptyState />
        ) : (
          <div className="space-y-2">
            {items.map((d) => (
              <DealershipCard
                key={d.id}
                teamId={Number(d.id)}
                name={d.name}
                address={d.address}
                city={d.city}
                state={d.state}
                zip={d.zip}
                phone={d.phone}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

function DealershipCard({
  teamId,
  name,
  address,
  city,
  state,
  zip,
  phone,
}: {
  teamId: number
  name: string
  address?: string
  city?: string
  state?: string
  zip?: string
  phone?: string
}) {
  const [expanded, setExpanded] = useState(false)
  const cs = [city, state].filter(Boolean).join(", ")
  const fullAddress = [address, cs, zip].filter(Boolean).join(", ")

  return (
    <div className="rounded-2xl border border-zinc-200/70 bg-white overflow-hidden hover:border-zinc-300 transition-colors duration-150">
      <button
        type="button"
        onClick={() => setExpanded((e) => !e)}
        aria-expanded={expanded}
        className="w-full px-4 py-3 flex items-center gap-3 text-left hover:bg-zinc-50/50 transition-colors duration-150"
      >
        <div className="size-10 rounded-xl bg-gradient-to-br from-sky-500 to-sky-700 grid place-items-center shrink-0 shadow-md shadow-sky-600/20">
          <Building07 className="size-5 text-white" aria-hidden />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-[14px] font-semibold text-zinc-900 truncate">{name}</p>
          <p className="text-[11.5px] text-zinc-500 truncate">
            {fullAddress || "Dealership"}
            {phone ? ` · ${phone}` : ""}
          </p>
        </div>
        <ChevronDown
          className={cx(
            "size-4 text-zinc-400 shrink-0 transition-transform duration-200",
            expanded && "rotate-180",
          )}
          aria-hidden
        />
      </button>
      <AnimatePresence initial={false}>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.18, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden"
          >
            <div className="border-t border-zinc-100 px-4 py-4 bg-zinc-50/30">
              <TeamMembersTableMini
                teamId={teamId}
                teamName={name}
                teamType="Dealership"
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

function EmptyState() {
  return (
    <div className="rounded-2xl border border-dashed border-zinc-200 bg-zinc-50/30 px-4 py-3 flex items-center gap-3">
      <div className="size-7 rounded-lg bg-white grid place-items-center shrink-0 shadow-sm">
        <Building07 className="size-4 text-zinc-400" aria-hidden />
      </div>
      <p className="text-[13px] font-semibold text-zinc-700 flex-1">
        No connected dealerships yet
      </p>
      <p className="text-[11.5px] text-zinc-500 max-w-[260px] text-right">
        When a dealership picks your shop, they'll appear here.
      </p>
    </div>
  )
}
